<template>
	<div class="draw-result-wrap">
		<!-- 生成中 -->
		<div class="result-card loading-card" v-if="_status === '1'">
			<div class="loading-inner">
				<div class="loading-canvas">
					<div class="shimmer-block"></div>
					<div class="loading-overlay">
						<div class="pulse-ring"></div>
						<i class="el-icon-picture-outline loading-icon"></i>
					</div>
				</div>
				<p class="loading-hint">AI 正在为你生成图像，约需 10~30 秒…</p>
			</div>
		</div>

		<!-- 已生成 -->
		<div class="result-card images-card" v-else-if="_status === '2' && _images.length > 0">
			<div class="images-header">
				<span class="images-title">生成结果</span>
				<span class="images-count">{{ _images.length }} 张</span>
			</div>
			<div :class="['images-grid', `grid-${Math.min(_images.length, 4)}`]">
				<div
					v-for="(img, idx) in _images"
					:key="idx"
					class="image-item"
					@click="preview(img)"
				>
					<img :src="img" :alt="'生成图像 ' + (idx + 1)" class="gen-image" />
					<div class="image-overlay">
						<i class="el-icon-zoom-in"></i>
						<span class="download-btn" @click.stop="downloadImage(img)">
							<i :class="downloadingMap[img] ? 'el-icon-loading' : 'el-icon-download'"></i>
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- 空态占位（精简版：作品占位 + 灵感建议） -->
		<div class="result-card empty-card" v-else-if="_status === '0'">
			<div class="empty-inner">
				<div class="empty-icon-wrap">
					<i class="el-icon-picture-outline"></i>
				</div>
				<p class="empty-title">作品将出现在这里</p>
				<p class="empty-hint">完成上方设置后点击 ↗ 生成按钮即可创作</p>

				<div class="example-block">
					<div class="example-label">没思路？试试这些灵感</div>
					<div class="example-tags">
						<span class="example-tag" v-for="t in examples" :key="t" @click="$emit('applyExample', t)">
							<i class="el-icon-magic-stick"></i>
							{{ t }}
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- 图片预览弹层（与历史画板保持一致：含参考图、关闭、下载） -->
		<el-dialog
			:visible.sync="previewVisible"
			width="auto"
			:fullscreen="false"
			custom-class="image-preview-dialog"
			:show-close="true"
			append-to-body
			center
		>
			<div class="preview-content">
				<el-image :src="previewSrc" fit="contain" class="preview-image"></el-image>
				<div class="preview-info">
					<p class="preview-prompt" v-if="_prompt">{{ _prompt }}</p>

					<!-- 参考图（图生图时展示） -->
					<div class="preview-reference" v-if="_referenceImage">
						<span class="preview-reference__label">
							<i class="el-icon-picture-outline"></i> 参考图
						</span>
						<el-image
							:src="_referenceImage"
							fit="cover"
							class="preview-reference__img"
							:preview-src-list="[_referenceImage]"
						></el-image>
					</div>

					<div class="preview-actions">
						<span
							class="preview-btn primary"
							:class="{ downloading: downloadingMap[previewSrc] }"
							@click="downloadImage(previewSrc)"
						>
							<i :class="downloadingMap[previewSrc] ? 'el-icon-loading' : 'el-icon-download'"></i>
							{{ downloadingMap[previewSrc] ? '下载中...' : '下载原图' }}
						</span>
						<el-button plain size="small" @click="previewVisible = false">关闭</el-button>
					</div>
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import { downloadDrawImageApi } from '@/apis/draw';

export default {
	name: 'layout-draw-result',

	props: {
		_images: { type: Array, default: () => [] },
		_status: { type: String, default: '0' },
		_referenceImage: { type: String, default: '' },
		_prompt: { type: String, default: '' },
	},

	data() {
		return {
			previewVisible: false,
			previewSrc: '',
			downloadingMap: {},
			examples: [
				'夕阳下的海滨小镇，写实风格',
				'赛博朋克城市夜景，霓虹灯',
				'可爱的柴犬在草地玩耍',
				'水墨山水画，中国风',
			],
		};
	},

	methods: {
		preview(src) {
			this.previewSrc = src;
			this.previewVisible = true;
		},

		async downloadImage(url) {
			if (!url || this.downloadingMap[url]) return;
			this.$set(this.downloadingMap, url, true);
			try {
				const blob = await downloadDrawImageApi(url);
				if (!blob) return;
				const blobUrl = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = blobUrl;
				a.download = 'ai-image-' + Date.now() + '.png';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(blobUrl);
			} catch {
				this.$message({ message: '下载失败，请稍后重试', type: 'error' });
			} finally {
				this.$delete(this.downloadingMap, url);
			}
		},
	},
};
</script>

<style lang="scss">
.draw-result-wrap {
	.result-card {
		background: #fff;
		border-radius: 16px;
		box-shadow: 0 2px 20px rgba(0, 0, 0, 0.07);
		overflow: hidden;
	}

	/* ---- 生成中 ---- */
	.loading-card {
		.loading-inner {
			padding: 40px 20px;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 20px;
		}

		.loading-canvas {
			position: relative;
			width: 100%;
			max-width: 480px;
			aspect-ratio: 1 / 1;
			border-radius: 12px;
			overflow: hidden;
		}

		.shimmer-block {
			width: 100%;
			height: 100%;
			background: linear-gradient(
				100deg,
				#f0f0f0 30%,
				#e4e4e4 50%,
				#f0f0f0 70%
			);
			background-size: 200% 100%;
			animation: shimmer 1.5s infinite;
		}

		.loading-overlay {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.pulse-ring {
			position: absolute;
			width: 80px;
			height: 80px;
			border-radius: 50%;
			border: 3px solid rgba(97, 38, 245, 0.3);
			animation: pulse-ring 1.8s ease-out infinite;
		}

		.loading-icon {
			font-size: 32px;
			color: rgba(97, 38, 245, 0.5);
		}

		.loading-hint {
			font-size: 14px;
			color: #999;
			margin: 0;
		}
	}

	/* ---- 图像网格 ---- */
	.images-card {
		padding: 20px;

		.images-header {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-bottom: 16px;

			.images-title {
				font-size: 15px;
				font-weight: 600;
				color: #1a1a1a;
			}

			.images-count {
				font-size: 12px;
				color: #999;
				background: #f4f5f7;
				padding: 2px 8px;
				border-radius: 10px;
			}
		}

		.images-grid {
			display: grid;
			gap: 10px;

			&.grid-1 { grid-template-columns: 1fr; }
			&.grid-2 { grid-template-columns: repeat(2, 1fr); }
			&.grid-3 { grid-template-columns: repeat(3, 1fr); }
			&.grid-4 { grid-template-columns: repeat(2, 1fr); }
		}

		.image-item {
			position: relative;
			border-radius: 10px;
			overflow: hidden;
			cursor: pointer;
			aspect-ratio: 1 / 1;
			background: #f4f5f7;

			.gen-image {
				width: 100%;
				height: 100%;
				object-fit: cover;
				display: block;
				transition: transform 0.3s;
			}

			.image-overlay {
				position: absolute;
				inset: 0;
				background: rgba(0, 0, 0, 0.4);
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 16px;
				opacity: 0;
				transition: opacity 0.2s;

				i {
					font-size: 22px;
					color: #fff;
				}

				.download-btn {
					color: #fff;
					font-size: 22px;
					line-height: 1;
				}
			}

			&:hover {
				.gen-image { transform: scale(1.03); }
				.image-overlay { opacity: 1; }
			}
		}
	}

	/* ---- 空态：精简版 ---- */
	.empty-card {
		.empty-inner {
			padding: 36px 24px 32px;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 4px;
		}

		.empty-icon-wrap {
			width: 56px;
			height: 56px;
			border-radius: 16px;
			background: linear-gradient(135deg, #f0f3f8 0%, #f4f0ff 100%);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 6px;

			i {
				font-size: 24px;
				color: #b0b8c5;
			}
		}

		.empty-title {
			font-size: 15px;
			font-weight: 600;
			color: #555;
			margin: 0;
		}

		.empty-hint {
			font-size: 12px;
			color: #aaa;
			margin: 0 0 20px;
			text-align: center;
		}

		// 灵感示例
		.example-block {
			width: 100%;
			max-width: 580px;
			margin-top: 4px;
			padding-top: 16px;
			border-top: 1px dashed #ecf0f6;
		}

		.example-label {
			font-size: 12px;
			color: #aaa;
			text-align: center;
			margin-bottom: 10px;
			letter-spacing: 0.3px;
		}

		.example-tags {
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			justify-content: center;
		}

		.example-tag {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			font-size: 12px;
			color: #6a5cf6;
			background: #f4f0ff;
			border: 1px solid #e0d6ff;
			border-radius: 16px;
			padding: 5px 12px;
			cursor: pointer;
			transition: all 0.15s;

			i { font-size: 12px; opacity: 0.75; }

			&:hover {
				background: #ebe3ff;
				border-color: #b89cf5;
				transform: translateY(-1px);
			}
		}
	}
}

/* ---- 预览弹层：复用历史画板的 .image-preview-dialog 样式，仅追加参考图区块 ---- */
.image-preview-dialog {
	.preview-content {
		.preview-info {
			.preview-reference {
				display: flex;
				align-items: center;
				gap: 10px;
				margin: 0 0 16px;
				padding: 10px 12px;
				background: #f7f8fb;
				border: 1px dashed #e0d6ff;
				border-radius: 10px;

				&__label {
					display: inline-flex;
					align-items: center;
					gap: 4px;
					font-size: 12px;
					color: #6a5cf6;
					white-space: nowrap;

					i { font-size: 14px; }
				}

				&__img {
					width: 64px;
					height: 64px;
					border-radius: 8px;
					overflow: hidden;
					background: #f0f0f0;
					cursor: zoom-in;
					flex-shrink: 0;
				}
			}
		}
	}
}

@keyframes shimmer {
	0%   { background-position: 200% 0; }
	100% { background-position: -200% 0; }
}

@keyframes pulse-ring {
	0%   { transform: scale(0.5); opacity: 1; }
	100% { transform: scale(2.5); opacity: 0; }
}
</style>
